import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { CookiesService } from './cookies.service';
import { EventService } from './event.service';
import { LoggerService } from './logger.service';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiResponse, AuthResponse } from '../interfaces/api-response';
import { User } from '../interfaces/user';
import { environment } from '../../../environments/environment';
import { Message } from "../helpers/message";
import { DeviceFingerprintService } from './device-fingerprint.service';
import { RememberMeStorageService } from './remember-me-storage.service';
import { ImpersonationService } from './impersonation.service';
import { TranslateService } from "@ngx-translate/core";

const message = new Message();

interface LoginData {
  grant_type: string;
  client_id: string;
  client_secret: string;
  username: string;
  password: string;
  scope: string;
  remember_me?: boolean;
  device_fingerprint?: string;
  remember_token?: string;
}

interface RegisterData {
  name?: string;
  email?: string;
  password?: string;
  'first-name'?: string;
  'last-name'?: string;
  position?: string;
  phone?: string;
  contacted?: string;
  city?: string;
  salutation?: string;
  first_name?: string;
  last_name?: string;
  contact_info?: string;
}

interface PasswordData {
  lang?: string;
  email?: string;
  token?: string;
  password?: string;
  password_confirmation?: string;
  old_password?: string;
  new_password?: string;
  current_password?: string;
  new_password_confirmation?: string;
}

interface ChannelManagerFilters {
  page?: number;
  perPage?: number;
  search?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private user: User = new User();

  constructor(
    private http: HttpService,
    private events: EventService,
    private cookies: CookiesService,
    private logger: LoggerService,
    private deviceFingerprint: DeviceFingerprintService,
    private rememberMeStorage: RememberMeStorageService,
    private impersonation: ImpersonationService,
    private translate: TranslateService
  ) {
    const userJson = this.cookies.get('user');
    const token = this.cookies.get('token');
    if (userJson) {
      try {
        const parsedUser = JSON.parse(userJson);
        this.user = parsedUser as User;
      } catch (error) {
        this.logger.error('Error parsing user data from cookies:', error);
      }
    } else if (token) {
      // Only fetch user data if the token is still valid — an expired token would
      // trigger the auth interceptor and redirect away from the current page.
      if (this.cookies.isTokenExpired()) {
        this.clearSessionLocally();
      } else {
        this.updateUser(token);
      }
    } else {
      this.logger.warn('No user data found in cookies');
    }
  }

  updateUser(token?: string): void {
    this.resolveUser(token).subscribe({
      error: (error) => message.error('Error from resolveUser:', error),
    });
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    if (this.cookies.isTokenExpired()) {
      this.logger.warn('Token expired, clearing session');
      this.clearSessionLocally();
      return false;
    }

    return true;
  }

  getToken(): string | null {
    return this.cookies.get('token');
  }

  isTokenExpired(): boolean {
    return this.cookies.isTokenExpired();
  }

  getTokenRemainingTime(): number {
    return this.cookies.getTokenRemainingTime();
  }

  getUser(): User {
    const userJson = this.cookies.get('user');
    if (userJson) {
      try {
        const parsedUser = JSON.parse(userJson);
        this.user = parsedUser as User;
      } catch {
        // JSON parsing failed — user cookie is corrupted or empty, keep existing user
      }
    }
    return this.user;
  }

  public async login(data: LoginData, rememberMe: boolean = false): Promise<Observable<AuthResponse | ApiResponse<User> | { error: unknown }>> {
    try {
      const fingerprint = await this.deviceFingerprint.getFingerprint();

      const loginData: LoginData = {
        ...data,
        remember_me: rememberMe,
        device_fingerprint: fingerprint
      };

      const existingRememberToken = this.rememberMeStorage.getRememberToken();
      if (existingRememberToken) {
        loginData.remember_token = existingRememberToken;
      }

      return this.http.post<AuthResponse>('v2/oauth/login', loginData).pipe(
        switchMap((result: AuthResponse) => {
          const tokenData = result?.data;

          const skipped2fa = tokenData?.skipped_2fa || result?.skipped_2fa;
          if (skipped2fa) {
            this.logger.info('Login successful - 2FA skipped (trusted device)');
          }

          const requires2fa = tokenData?.requires_2fa || result?.requires_2fa;
          const tempToken = tokenData?.temp_token || result?.temp_token;
          const rememberMeRequested = tokenData?.remember_me_requested || result?.remember_me_requested;

          if (requires2fa && tempToken) {
            this.storeTempToken(tempToken);

            if (rememberMeRequested) {
              sessionStorage.setItem('remember_me_pending', 'true');
            }

            return of(result);
          }

          const accessToken = tokenData?.access_token;
          if (accessToken) {
            this.cookies.update('token', accessToken);

            const expiresIn = tokenData?.expires_in;
            if (expiresIn) {
              this.cookies.setTokenExpiry(expiresIn);
            }

            const rememberToken = tokenData?.remember_token || result?.remember_token;
            const rememberExpiresAt = tokenData?.remember_expires_at || result?.remember_expires_at;

            if (rememberToken) {
              this.rememberMeStorage.setRememberToken(rememberToken, rememberExpiresAt);
              this.logger.info('Device trusted - Remember Me enabled for 30 days');
            }

            return this.resolveUser();
          } else {
            return of(result);
          }
        }),
      );
    } catch (error) {
      this.logger.error('Error generating device fingerprint:', error);
      return this.http.post<AuthResponse>('v2/oauth/login', data).pipe(
        switchMap((result: AuthResponse) => {
          const tokenData = result?.data;
          const requires2fa = tokenData?.requires_2fa || result?.requires_2fa;
          const tempToken = tokenData?.temp_token || result?.temp_token;

          if (requires2fa && tempToken) {
            this.storeTempToken(tempToken);
            return of(result);
          }

          const accessToken = tokenData?.access_token;
          if (accessToken) {
            this.cookies.update('token', accessToken);
            if (tokenData?.expires_in) {
              this.cookies.setTokenExpiry(tokenData.expires_in);
            }
            return this.resolveUser();
          }

          return of(result);
        }),
      );
    }
  }

  public async verify2FA(
    tempToken: string,
    code: string,
    isRecovery: boolean,
    rememberMe: boolean = false
  ): Promise<Observable<AuthResponse | ApiResponse<User> | { error: unknown }>> {
    try {
      const fingerprint = await this.deviceFingerprint.getFingerprint();

      const rememberMePending = sessionStorage.getItem('remember_me_pending') === 'true';
      const shouldRemember = rememberMe || rememberMePending;

      const payload = {
        temp_token: tempToken,
        code,
        recovery: isRecovery,
        remember_me: shouldRemember,
        device_fingerprint: fingerprint
      };

      return this.http.post<AuthResponse>('v2/oauth/verify-2fa', payload).pipe(
        switchMap((result: AuthResponse) => {
          const tokenData = result?.data;

          if (tokenData?.access_token) {
            this.clearTempToken();
            sessionStorage.removeItem('remember_me_pending');

            this.cookies.update('token', tokenData.access_token);

            if (tokenData.expires_in) {
              this.cookies.setTokenExpiry(tokenData.expires_in);
            }

            const rememberToken = tokenData?.remember_token || result?.remember_token;
            const rememberExpiresAt = tokenData?.remember_expires_at || result?.remember_expires_at;
            const deviceName = tokenData?.device_name || result?.device_name;

            if (rememberToken) {
              this.rememberMeStorage.setRememberToken(rememberToken, rememberExpiresAt);
              this.logger.info(`Device trusted as "${deviceName}" - 2FA will be skipped for 30 days`);
            }

            return this.resolveUser();
          } else {
            return of(result);
          }
        }),
      );
    } catch (error) {
      this.logger.error('Error during 2FA verification:', error);
      throw error;
    }
  }

  public hasRememberMe(): boolean {
    return this.rememberMeStorage.hasValidRememberToken();
  }

  public getRememberMeInfo(): { active: boolean; expiresAt: Date | null; daysRemaining: number | null } {
    return {
      active: this.rememberMeStorage.hasValidRememberToken(),
      expiresAt: this.rememberMeStorage.getRememberTokenExpiry(),
      daysRemaining: this.rememberMeStorage.getRemainingDays()
    };
  }

  getTempToken(): string | null {
    return sessionStorage.getItem('2fa_temp_token');
  }

  private storeTempToken(token: string): void {
    sessionStorage.setItem('2fa_temp_token', token);
  }

  clearTempToken(): void {
    sessionStorage.removeItem('2fa_temp_token');
  }

  clearPendingTwoFactorState(): void {
    this.clearTempToken();
    sessionStorage.removeItem('remember_me_pending');
  }

  getEmptyLoginForm(): LoginData {
    return JSON.parse(JSON.stringify(environment.api.oauth));
  }

  register(data: RegisterData): Observable<AuthResponse | ApiResponse<User> | { error: unknown }> {
    const dataCopy = JSON.parse(JSON.stringify(data));
    return this.http.post<AuthResponse>('v2/extranet/hotels/register', dataCopy).pipe(
      switchMap((result: AuthResponse) => {
        const tokenData = result?.data;

        if (tokenData?.access_token) {
          this.cookies.update('token', tokenData.access_token);

          if (tokenData.expires_in) {
            this.cookies.setTokenExpiry(tokenData.expires_in);
          }

          return this.resolveUser();
        } else {
          return of(result);
        }
      }),
      catchError((error) => of({ error }))
    );
  }

  resolveUser(token?: string): Observable<ApiResponse<User> | { error: unknown }> {
    if (token) {
      this.cookies.update('token', token);
    }
    return this.http.get<ApiResponse<User>>('v2/extranet/me').pipe(
      map((result: ApiResponse<User>) => {
        if (result && !result.error && !result.errors && result.data) {
          const userString = JSON.stringify(result.data);
          this.cookies.update('user', userString);
          this.user = result.data;
          this.events.broadcast('userChanged', result.data);
        } else {
          this.cookies.deleteUser();
          message.error('User failed authentication', result);
        }
        return result;
      }),
      catchError((error) => {
        return of({ error });
      })
    );
  }

  setToken(token: string): void {
    if (token) {
      this.cookies.update('token', token);
    }
  }

  // Locale for these three endpoints comes from the `locale` query param
  // that localeInterceptor already attaches to every request — no need to
  // send `lang` in the body too (the backend's 'locale.request' middleware
  // doesn't read the body, so it was ignored either way).
  forgetPassword(data: PasswordData): Observable<unknown> {
    return this.http.post('v2/users/forgot-password', data);
  }

  validateResetToken(token: string): Observable<{ success: boolean; email: string }> {
    return this.http.post<{ success: boolean; email: string }>(
      `v2/users/validate-reset-token`,
      { token }
    );
  }

  resetPassword(data: PasswordData): Observable<unknown> {
    return this.http.post('v2/users/reset-password', {
      token: data.token,
      email: data.email,
      password: data.password,
      password_confirmation: data.password_confirmation
    });
  }

  changePassword(data: PasswordData): Observable<unknown> {
    const payload = {
      current_password: data.current_password,
      new_password: data.new_password,
      new_password_confirmation: data.new_password_confirmation
    };
    return this.http.put('v2/extranet/users/change-password', payload);
  }

  updateProfile(data: { first_name: string; last_name: string }): Observable<unknown> {
    return this.http.put('v2/extranet/users/profile', data);
  }

  /**
   * Clears all local session data immediately (synchronous).
   * Use this when you need guaranteed cleanup without waiting for an API call —
   * for example inside guards or interceptors where subscribing to an Observable
   * is not possible.
   */
  public clearSessionLocally(clearRememberMe: boolean = false): void {
    if (clearRememberMe) {
      this.rememberMeStorage.clearAll();
      this.logger.info('Remember Me cleared on logout');
    }

    const userId = this.user?.id;
    if (userId) {
      localStorage.removeItem(`hc_selected_hotel_${userId}`);
    }
    localStorage.removeItem('lastHotelId');

    this.cookies.deleteUser();
    this.impersonation.endImpersonation();
    this.events.broadcast('userChanged', null);
  }

  /**
   * Logs the user out: clears local session AND revokes the token on the backend.
   * Always subscribe to this Observable — the cleanup only runs when subscribed.
   * Use clearSessionLocally() instead when subscription is not possible.
   */
  public logout(clearRememberMe: boolean = false): Observable<{ success: boolean }> {
    const token = this.getToken();

    if (!token) {
      this.clearSessionLocally(clearRememberMe);
      return of({ success: true });
    }

    return this.http.post('v2/oauth/logout', {}).pipe(
      map(() => {
        this.clearSessionLocally(clearRememberMe);
        return { success: true };
      }),
      catchError((error) => {
        // Even if the API call fails, always clean up locally
        this.logger.error('Logout API call failed, clearing session anyway:', error);
        this.clearSessionLocally(clearRememberMe);
        return of({ success: true });
      })
    );
  }
}
